import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/api';
import { toast } from 'sonner';
import { Plus, FolderKanban, Building2, Mail, Settings, CheckCircle, XCircle, Trash2, Eye, Edit2, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import SISummaryWidget from '@/components/SISummaryWidget';
import RFISummaryWidget from '@/components/RFISummaryWidget';
import ClientForm from '@/components/ClientForm';
import AdminDashboardOverview from '@/components/AdminDashboardOverview';
import EventConfigPanel from '@/components/EventConfigPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user } = useAuth();
  const { isAdmin, canCreateClients, canCreateProjects, canManageSMTP } = usePermissions();
  const [activeTab, setActiveTab] = useState('overview');
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Client dialog state
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  // Project dialog state
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: '', client_id: '', description: '', contract_type: 'JBCC', contract_family: 'JBCC', contract_form: '', contract_value: '', project_number: '', contractor_name: '', start_date: '', end_date: '', sectional_completion: false, location: null });
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState({ id: '', name: '', client_id: '', description: '', contract_type: '', contract_number: '', project_number: '', contractor_name: '', location: null });
  const [showAdvancedRules, setShowAdvancedRules] = useState(false);
  const [customRules, setCustomRules] = useState(null);
  const [contractTypes, setContractTypes] = useState([]);
  const [allClauses, setAllClauses] = useState([]);
  const [showClausePanel, setShowClausePanel] = useState(false);
  const [expandedClauses, setExpandedClauses] = useState({});
  const [editingSubClause, setEditingSubClause] = useState(null);
  const [editText, setEditText] = useState('');
  const [addingToClause, setAddingToClause] = useState(null);
  const [newSubClauseNum, setNewSubClauseNum] = useState('');
  const [newSubClauseText, setNewSubClauseText] = useState('');

  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'contractor', client_id: '' });

  // SMTP Settings state
  const [smtpSettings, setSmtpSettings] = useState([]);
  const [systemSmtp, setSystemSmtp] = useState(null);
  const [smtpDialogOpen, setSmtpDialogOpen] = useState(false);
  const [systemSmtpDialogOpen, setSystemSmtpDialogOpen] = useState(false);
  const [selectedClientForSmtp, setSelectedClientForSmtp] = useState('');
  const [smtpForm, setSmtpForm] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    use_tls: true
  });
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    loadData();
    
    // Set initial tab based on hash
    const initialHash = window.location.hash.replace('#', '');
    if (initialHash && ['overview', 'clients', 'projects', 'settings', 'event-config'].includes(initialHash)) {
      setActiveTab(initialHash);
    } else {
      // Default to overview and set hash
      setActiveTab('overview');
      if (!window.location.hash) {
        window.location.hash = 'overview';
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && ['overview', 'clients', 'projects', 'settings', 'event-config'].includes(hash)) {
        setActiveTab(hash);
      } else {
        setActiveTab('overview');
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const responses = await Promise.all([
        api.clients.getAll(),
        api.projects.getAll(),
        api.smtp.getSettings(),
        api.smtp.getSystemSettings().catch(() => ({ data: null })),
      ]);
      setClients(responses[0].data);
      setProjects(responses[1].data);
      // SMTP settings returns array for admin, single for contractor
      const smtpData = responses[2].data;
      // Filter out system default from per-client list
      const clientSmtpData = Array.isArray(smtpData) 
        ? smtpData.filter(s => s.client_id !== 'SYSTEM_DEFAULT') 
        : (smtpData ? [smtpData] : []);
      setSmtpSettings(clientSmtpData);
      setSystemSmtp(responses[3].data);
      
      // Load contract types
      try {
        const ctRes = await api.contracts.getTypes();
        setContractTypes(ctRes.data || []);
      } catch { /* non-critical */ }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get defaults for a contract type
  const getContractDefaults = (ctValue) => {
    const ct = contractTypes.find(c => c.value === ctValue);
    if (!ct) return null;
    return {
      notice_period: ct.notice_period,
      claim_period: ct.claim_period,
      determination_period: ct.determination_period,
      determination_consequence: ct.determination_consequence,
      determination_consequence_text: ct.determination_consequence_text,
      determination_clause: ct.determination_clause,
      time_bar_consequence: ct.time_bar_consequence,
    };
  };

  const handleContractTypeChange = (newType) => {
    setProjectForm({ ...projectForm, contract_type: newType, contract_family: newType });
    setCustomRules(null);
    setShowAdvancedRules(false);
    setShowClausePanel(false);
    setAllClauses([]);
    setExpandedClauses({});
  };

  const fetchAllClauses = async (contractType) => {
    try {
      const res = await api.contracts.getAllClauses(contractType);
      setAllClauses(res.data || []);
    } catch {
      setAllClauses([]);
    }
  };

  const toggleClausePanel = () => {
    const next = !showClausePanel;
    setShowClausePanel(next);
    if (next && allClauses.length === 0) {
      fetchAllClauses(projectForm.contract_type);
    }
  };

  const getCustomClauses = () => {
    return (customRules || {}).custom_clauses || {};
  };

  const setCustomClauses = (newCustomClauses) => {
    const defaults = getContractDefaults(projectForm.contract_type);
    const current = customRules || defaults || {};
    setCustomRules({ ...current, custom_clauses: newCustomClauses });
  };

  const handleAmendSubClause = (clauseNum, subNum, newText) => {
    const cc = getCustomClauses();
    const clauseAmend = cc[clauseNum] || {};
    const amended = { ...(clauseAmend.amended_sub_clauses || {}), [subNum]: newText };
    setCustomClauses({ ...cc, [clauseNum]: { ...clauseAmend, amended_sub_clauses: amended } });
    setEditingSubClause(null);
    setEditText('');
  };

  const handleAddSubClause = (clauseNum) => {
    if (!newSubClauseNum.trim() || !newSubClauseText.trim()) return;
    const cc = getCustomClauses();
    const clauseAmend = cc[clauseNum] || {};
    const added = { ...(clauseAmend.added_sub_clauses || {}), [newSubClauseNum.trim()]: newSubClauseText.trim() };
    setCustomClauses({ ...cc, [clauseNum]: { ...clauseAmend, added_sub_clauses: added } });
    setAddingToClause(null);
    setNewSubClauseNum('');
    setNewSubClauseText('');
  };

  const handleRemoveCustomSubClause = (clauseNum, subNum) => {
    const cc = getCustomClauses();
    const clauseAmend = cc[clauseNum] || {};
    // Check if it's in added_sub_clauses
    if (clauseAmend.added_sub_clauses && clauseAmend.added_sub_clauses[subNum]) {
      const { [subNum]: _, ...rest } = clauseAmend.added_sub_clauses;
      const updated = { ...clauseAmend, added_sub_clauses: rest };
      if (Object.keys(rest).length === 0) delete updated.added_sub_clauses;
      const newCc = { ...cc, [clauseNum]: updated };
      if (!updated.amended_sub_clauses && !updated.added_sub_clauses) delete newCc[clauseNum];
      setCustomClauses(newCc);
    }
    // Check if it's in amended_sub_clauses (revert to default)
    if (clauseAmend.amended_sub_clauses && clauseAmend.amended_sub_clauses[subNum]) {
      const { [subNum]: _, ...rest } = clauseAmend.amended_sub_clauses;
      const updated = { ...clauseAmend, amended_sub_clauses: rest };
      if (Object.keys(rest).length === 0) delete updated.amended_sub_clauses;
      const newCc = { ...cc, [clauseNum]: updated };
      if (!updated.amended_sub_clauses && !updated.added_sub_clauses) delete newCc[clauseNum];
      setCustomClauses(newCc);
    }
  };

  const isSubClauseAmended = (clauseNum, subNum) => {
    const cc = getCustomClauses();
    return !!(cc[clauseNum]?.amended_sub_clauses?.[subNum]);
  };

  const isSubClauseAdded = (clauseNum, subNum) => {
    const cc = getCustomClauses();
    return !!(cc[clauseNum]?.added_sub_clauses?.[subNum]);
  };

  const getEffectiveSubClauseText = (clauseNum, subNum, defaultText) => {
    const cc = getCustomClauses();
    if (cc[clauseNum]?.amended_sub_clauses?.[subNum]) {
      return cc[clauseNum].amended_sub_clauses[subNum];
    }
    if (cc[clauseNum]?.added_sub_clauses?.[subNum]) {
      return cc[clauseNum].added_sub_clauses[subNum];
    }
    return defaultText;
  };

  const handleCustomRuleChange = (field, value) => {
    const defaults = getContractDefaults(projectForm.contract_type);
    const current = customRules || defaults || {};
    setCustomRules({ ...current, [field]: value });
  };

  const handleCustomPeriodChange = (periodKey, subField, value) => {
    const defaults = getContractDefaults(projectForm.contract_type);
    const current = customRules || defaults || {};
    const currentPeriod = current[periodKey] || defaults?.[periodKey] || {};
    setCustomRules({
      ...current,
      [periodKey]: { ...currentPeriod, [subField]: subField === 'value' ? parseInt(value) || 0 : value }
    });
  };

  const handleSaveSystemSmtp = async (e) => {
    e.preventDefault();
    try {
      await api.smtp.createSystemSettings(smtpForm);
      toast.success('System SMTP settings saved successfully');
      setSystemSmtpDialogOpen(false);
      setSmtpForm({
        smtp_host: '',
        smtp_port: 587,
        smtp_user: '',
        smtp_password: '',
        from_email: '',
        from_name: '',
        use_tls: true
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save system SMTP settings');
    }
  };

  const handleSaveSmtpSettings = async (e) => {
    e.preventDefault();
    try {
      await api.smtp.createSettingsForClient(selectedClientForSmtp, smtpForm);
      toast.success('SMTP settings saved successfully');
      setSmtpDialogOpen(false);
      setSmtpForm({
        smtp_host: '',
        smtp_port: 587,
        smtp_user: '',
        smtp_password: '',
        from_email: '',
        from_name: '',
        use_tls: true
      });
      setSelectedClientForSmtp('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
    }
  };

  const handleSaveClient = async (formData) => {
    try {
      if (editingClient) {
        await api.clients.update(editingClient.id, formData);
        toast.success('Client updated successfully');
      } else {
        await api.clients.create(formData);
        toast.success('Client created successfully');
      }
      setClientDialogOpen(false);
      setEditingClient(null);
      loadData();
    } catch (error) {
      toast.error(editingClient ? 'Failed to update client' : 'Failed to create client');
      throw error;
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setClientDialogOpen(true);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...projectForm };
      if (payload.contract_value) payload.contract_value = parseFloat(payload.contract_value);
      if (customRules) {
        payload.custom_contract_rules = customRules;
      }
      const res = await api.projects.create(payload);
      const newProjectId = res.data?.id || res.data?.project?.id;
      toast.success('Project created! Redirecting to Document Control Setup...');
      setProjectDialogOpen(false);
      setProjectForm({ name: '', client_id: '', description: '', contract_type: 'JBCC', contract_family: 'JBCC', contract_form: '', contract_value: '', project_number: '', contractor_name: '', start_date: '', end_date: '', sectional_completion: false, location: null });
      setCustomRules(null);
      setShowAdvancedRules(false);
      loadData();
      if (newProjectId) {
        navigate(`/projects/${newProjectId}/setup`);
      }
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const data = { ...userForm };
      if (userForm.role !== 'contractor') {
        delete data.client_id;
      }
      await api.users.create(data);
      toast.success('User created successfully');
      setUserDialogOpen(false);
      setUserForm({ name: '', email: '', password: '', role: 'contractor', client_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-barlow font-bold uppercase tracking-tight mb-2">Admin Dashboard</h1>
          <p className="text-slate-600 text-sm sm:text-base">Manage clients, projects, and users</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Total Clients</p>
                <p className="text-3xl font-barlow font-bold">{clients.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-slate-400" strokeWidth={1.5} />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Active Projects</p>
                <p className="text-3xl font-barlow font-bold">{projects.length}</p>
              </div>
              <FolderKanban className="h-8 w-8 text-slate-400" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        {/* Mobile Tab Dropdown */}
        <div className="lg:hidden mb-6">
          <Select 
            value={activeTab} 
            onValueChange={(value) => {
              window.location.hash = value;
            }}
          >
            <SelectTrigger className="w-full" data-testid="mobile-dashboard-tab-selector">
              <SelectValue placeholder="Select Section" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="overview">Overview</SelectItem>
              {isAdmin() && <SelectItem value="clients">Clients</SelectItem>}
              <SelectItem value="projects">Projects</SelectItem>
              {canManageSMTP() && <SelectItem value="settings">Email Settings</SelectItem>}
              {isAdmin() && <SelectItem value="event-config">Event Config</SelectItem>}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop Tab Navigation */}
        <div className="hidden lg:flex gap-2 mb-6 border-b border-slate-200">
          <button
            onClick={() => window.location.hash = 'overview'}
            className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
              activeTab === 'overview'
                ? 'border-b-2 border-slate-900 text-slate-900'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            overview
          </button>
          {isAdmin() && (
            <button
              onClick={() => window.location.hash = 'clients'}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === 'clients'
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              clients
            </button>
          )}
          <button
            onClick={() => window.location.hash = 'projects'}
            className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
              activeTab === 'projects'
                ? 'border-b-2 border-slate-900 text-slate-900'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            projects
          </button>
          {canManageSMTP() && (
            <button
              onClick={() => window.location.hash = 'settings'}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === 'settings'
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <Mail className="h-4 w-4 inline mr-1" />
              email settings
            </button>
          )}
          {isAdmin() && (
            <button
              onClick={() => window.location.hash = 'event-config'}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === 'event-config'
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
              data-testid="event-config-tab"
            >
              <Settings className="h-4 w-4 inline mr-1" />
              event config
            </button>
          )}
        </div>

        {activeTab === 'overview' && (
          <AdminDashboardOverview projects={projects} />
        )}

        {activeTab === 'clients' && isAdmin() && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-barlow font-semibold uppercase">Clients</h3>
              <Dialog open={clientDialogOpen} onOpenChange={(open) => {
                setClientDialogOpen(open);
                if (!open) {
                  setEditingClient(null);
                }
              }}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-client-button" 
                    className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Client
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-barlow font-bold uppercase">
                      {editingClient ? 'Edit Client' : 'Add New Client'}
                    </DialogTitle>
                  </DialogHeader>
                  <ClientForm 
                    client={editingClient}
                    onSave={handleSaveClient}
                    onCancel={() => {
                      setClientDialogOpen(false);
                      setEditingClient(null);
                    }}
                  />
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <div key={client.id} className={`bg-white border rounded-sm shadow-sm p-6 hover:shadow-md transition-shadow ${!client.is_active ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}>
                  <div className="flex items-start gap-3">
                    {/* Client Logo */}
                    {client.logo_url && (
                      <div className="w-12 h-12 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                        <img 
                          src={client.logo_url.startsWith('/api/') ? `${BACKEND_URL}${client.logo_url}` : `${BACKEND_URL}/api${client.logo_url}`} 
                          alt={client.name} 
                          className="w-full h-full object-contain"
                          onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/clients/${client.id}/dashboard`)}>
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-lg hover:text-blue-600 transition-colors truncate">{client.name}</h4>
                        {!client.is_active && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Inactive</span>
                        )}
                      </div>
                      {client.legal_name && client.legal_name !== client.name && (
                        <p className="text-xs text-slate-400">{client.legal_name}</p>
                      )}
                      <p className="text-sm text-slate-600 truncate">{client.email}</p>
                      {client.phone && <p className="text-sm text-slate-600 font-mono">{client.phone}</p>}
                      {/* Show certifications if available */}
                      {(client.certification?.cidb_grade || client.certification?.bee_level) && (
                        <div className="flex gap-2 mt-2">
                          {client.certification?.cidb_grade && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              CIDB: {client.certification.cidb_grade}
                            </span>
                          )}
                          {client.certification?.bee_level && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              BEE: {client.certification.bee_level}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        onClick={() => handleEditClient(client)}
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={async () => {
                          if (window.confirm(`Are you sure you want to delete client "${client.name}"? This may affect associated projects.`)) {
                            try {
                              await api.clients.delete(client.id);
                              toast.success('Client deleted');
                              loadData();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to delete client');
                            }
                          }
                        }}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-barlow font-semibold uppercase">Projects</h3>
              {isAdmin() && (
                <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="add-project-button" 
                      className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Project
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="text-xl font-barlow font-bold uppercase">Add New Project</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateProject} className="space-y-4">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Project Name</Label>
                        <Input
                          value={projectForm.name}
                          onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                          required
                          className="rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Project Number</Label>
                        <Input
                          value={projectForm.project_number}
                          onChange={(e) => setProjectForm({ ...projectForm, project_number: e.target.value })}
                          placeholder="e.g. PRJ-2026-001"
                          className="rounded-sm"
                          data-testid="project-number-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Client</Label>
                        <Select value={projectForm.client_id} onValueChange={(value) => setProjectForm({ ...projectForm, client_id: value })}>
                          <SelectTrigger className="rounded-sm">
                            <SelectValue placeholder="Select a client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Description</Label>
                        <Input
                          value={projectForm.description}
                          onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                          className="rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Project Location</Label>
                        <LocationAutocomplete
                          value={projectForm.location}
                          onChange={(loc) => setProjectForm({ ...projectForm, location: loc })}
                        />
                        {projectForm.location && (
                          <p className="text-xs text-green-600 mt-1" data-testid="location-selected">
                            Location set: {projectForm.location.lat.toFixed(4)}, {projectForm.location.lng.toFixed(4)}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Contractor Name</Label>
                        <Input
                          value={projectForm.contractor_name}
                          onChange={(e) => setProjectForm({ ...projectForm, contractor_name: e.target.value })}
                          placeholder="e.g. Murray & Roberts"
                          className="rounded-sm"
                          data-testid="contractor-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Contract Value (R)</Label>
                          <Input
                            type="number"
                            value={projectForm.contract_value}
                            onChange={(e) => setProjectForm({ ...projectForm, contract_value: e.target.value })}
                            placeholder="e.g. 250000000"
                            className="rounded-sm"
                            data-testid="contract-value-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Sectional Completion</Label>
                          <select
                            value={projectForm.sectional_completion ? 'yes' : 'no'}
                            onChange={(e) => setProjectForm({ ...projectForm, sectional_completion: e.target.value === 'yes' })}
                            className="flex h-10 w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                            data-testid="sectional-completion-select"
                          >
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Start Date</Label>
                          <Input
                            type="date"
                            value={projectForm.start_date}
                            onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })}
                            className="rounded-sm"
                            data-testid="start-date-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">End Date</Label>
                          <Input
                            type="date"
                            value={projectForm.end_date}
                            onChange={(e) => setProjectForm({ ...projectForm, end_date: e.target.value })}
                            className="rounded-sm"
                            data-testid="end-date-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Contract Type</Label>
                        <select
                          value={projectForm.contract_type}
                          onChange={(e) => handleContractTypeChange(e.target.value)}
                          className="flex h-10 w-full rounded-sm border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          data-testid="contract-type-select"
                        >
                          <option value="JBCC">JBCC PBA 6.2</option>
                          <option value="FIDIC 1999">FIDIC 1999 Red Book</option>
                          <option value="NEC3">NEC3 ECC</option>
                          <option value="NEC4">NEC4 ECC</option>
                        </select>
                      </div>

                      {/* Advanced Contract Rules (Amendable) */}
                      <div className="border border-slate-200 rounded-sm">
                        <button
                          type="button"
                          onClick={() => setShowAdvancedRules(!showAdvancedRules)}
                          className="w-full flex items-center justify-between px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-50 transition-colors"
                          data-testid="toggle-advanced-rules"
                        >
                          <span className="flex items-center gap-2">
                            <Settings className="h-3.5 w-3.5" />
                            Contract Amendments (Optional)
                          </span>
                          {showAdvancedRules ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        
                        {showAdvancedRules && (() => {
                          const defaults = getContractDefaults(projectForm.contract_type);
                          const ct = contractTypes.find(c => c.value === projectForm.contract_type);
                          const dmName = ct?.terminology?.decision_maker || 'Decision Maker';
                          const vals = customRules || defaults || {};
                          
                          return (
                            <div className="px-4 pb-4 space-y-4 border-t border-slate-100" data-testid="advanced-rules-panel">
                              <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                                <Info className="h-3 w-3 shrink-0" />
                                Amend default contract periods and consequences. Leave unchanged to use standard terms.
                              </p>
                              
                              {/* Notice Period */}
                              <div>
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">
                                  Notice Period
                                </Label>
                                <div className="flex gap-2">
                                  <Input
                                    type="number"
                                    min="1"
                                    value={vals.notice_period?.value || ''}
                                    onChange={(e) => handleCustomPeriodChange('notice_period', 'value', e.target.value)}
                                    className="rounded-sm w-24"
                                    placeholder="Days"
                                    data-testid="notice-period-value"
                                  />
                                  <select
                                    value={vals.notice_period?.unit || 'calendar_days'}
                                    onChange={(e) => handleCustomPeriodChange('notice_period', 'unit', e.target.value)}
                                    className="flex h-10 rounded-sm border border-input bg-background px-2 py-2 text-xs"
                                    data-testid="notice-period-unit"
                                  >
                                    <option value="working_days">Working Days</option>
                                    <option value="calendar_days">Calendar Days</option>
                                    <option value="weeks">Weeks</option>
                                  </select>
                                </div>
                              </div>
                              
                              {/* Claim / Quotation Period */}
                              <div>
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">
                                  {ct?.terminology?.claim || 'Claim'} Period
                                </Label>
                                <div className="flex gap-2">
                                  <Input
                                    type="number"
                                    min="1"
                                    value={vals.claim_period?.value || vals.detailed_claim_period?.value || vals.quotation_period?.value || ''}
                                    onChange={(e) => {
                                      const periodKey = projectForm.contract_type === 'FIDIC 1999' ? 'detailed_claim_period' : 
                                                       projectForm.contract_type.startsWith('NEC') ? 'quotation_period' : 'claim_period';
                                      handleCustomPeriodChange(periodKey, 'value', e.target.value);
                                    }}
                                    className="rounded-sm w-24"
                                    placeholder="Days"
                                    data-testid="claim-period-value"
                                  />
                                  <select
                                    value={vals.claim_period?.unit || vals.detailed_claim_period?.unit || vals.quotation_period?.unit || 'calendar_days'}
                                    onChange={(e) => {
                                      const periodKey = projectForm.contract_type === 'FIDIC 1999' ? 'detailed_claim_period' : 
                                                       projectForm.contract_type.startsWith('NEC') ? 'quotation_period' : 'claim_period';
                                      handleCustomPeriodChange(periodKey, 'unit', e.target.value);
                                    }}
                                    className="flex h-10 rounded-sm border border-input bg-background px-2 py-2 text-xs"
                                    data-testid="claim-period-unit"
                                  >
                                    <option value="working_days">Working Days</option>
                                    <option value="calendar_days">Calendar Days</option>
                                    <option value="weeks">Weeks</option>
                                  </select>
                                </div>
                              </div>
                              
                              {/* Determination / Response Period */}
                              <div>
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">
                                  {dmName} Response Period
                                </Label>
                                <div className="flex gap-2">
                                  <Input
                                    type="number"
                                    min="1"
                                    value={vals.determination_period?.value || vals.engineer_response_period?.value || vals.pm_assessment_period?.value || ''}
                                    onChange={(e) => {
                                      const periodKey = projectForm.contract_type === 'JBCC' ? 'determination_period' : 
                                                       projectForm.contract_type === 'FIDIC 1999' ? 'engineer_response_period' : 'pm_assessment_period';
                                      handleCustomPeriodChange(periodKey, 'value', e.target.value);
                                    }}
                                    className="rounded-sm w-24"
                                    placeholder="Days"
                                    data-testid="determination-period-value"
                                  />
                                  <select
                                    value={vals.determination_period?.unit || vals.engineer_response_period?.unit || vals.pm_assessment_period?.unit || 'calendar_days'}
                                    onChange={(e) => {
                                      const periodKey = projectForm.contract_type === 'JBCC' ? 'determination_period' : 
                                                       projectForm.contract_type === 'FIDIC 1999' ? 'engineer_response_period' : 'pm_assessment_period';
                                      handleCustomPeriodChange(periodKey, 'unit', e.target.value);
                                    }}
                                    className="flex h-10 rounded-sm border border-input bg-background px-2 py-2 text-xs"
                                    data-testid="determination-period-unit"
                                  >
                                    <option value="working_days">Working Days</option>
                                    <option value="calendar_days">Calendar Days</option>
                                    <option value="weeks">Weeks</option>
                                  </select>
                                </div>
                              </div>
                              
                              {/* Consequence of no response */}
                              <div>
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">
                                  If {dmName} Fails to Respond
                                </Label>
                                <select
                                  value={vals.determination_consequence || ''}
                                  onChange={(e) => handleCustomRuleChange('determination_consequence', e.target.value)}
                                  className="flex h-10 w-full rounded-sm border border-input bg-background px-3 py-2 text-xs"
                                  data-testid="determination-consequence-select"
                                >
                                  <option value="deemed_rejected">Deemed Rejected</option>
                                  <option value="deemed_accepted">Deemed Accepted</option>
                                  <option value="referable_to_dab">Referable to DAB / Adjudicator</option>
                                </select>
                              </div>
                              
                              {/* Consequence clause reference */}
                              <div>
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">
                                  Clause Reference
                                </Label>
                                <Input
                                  value={vals.determination_clause || ''}
                                  onChange={(e) => handleCustomRuleChange('determination_clause', e.target.value)}
                                  className="rounded-sm text-xs"
                                  placeholder="e.g. 23.6 (amended)"
                                  data-testid="determination-clause-input"
                                />
                              </div>
                              
                              {/* Custom consequence text */}
                              <div>
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">
                                  Consequence Text (as per amendment)
                                </Label>
                                <textarea
                                  value={vals.determination_consequence_text || ''}
                                  onChange={(e) => handleCustomRuleChange('determination_consequence_text', e.target.value)}
                                  className="flex w-full rounded-sm border border-input bg-background px-3 py-2 text-xs min-h-[60px] ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  placeholder="Describe the amended consequence..."
                                  data-testid="determination-consequence-text"
                                />
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Clauses & Sub-Clauses Customization */}
                      <div className="border border-slate-200 rounded-sm">
                        <button
                          type="button"
                          onClick={toggleClausePanel}
                          className="w-full flex items-center justify-between px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-50 transition-colors"
                          data-testid="toggle-clause-panel"
                        >
                          <span className="flex items-center gap-2">
                            <Settings className="h-3.5 w-3.5" />
                            Clauses & Sub-Clauses
                            {Object.keys(getCustomClauses()).length > 0 && (
                              <span className="bg-amber-100 text-amber-700 text-[9px] font-bold px-1.5 py-0.5 rounded">
                                {Object.keys(getCustomClauses()).length} amended
                              </span>
                            )}
                          </span>
                          {showClausePanel ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>

                        {showClausePanel && (
                          <div className="px-4 pb-4 border-t border-slate-100" data-testid="clause-panel">
                            <p className="text-xs text-slate-500 mt-3 mb-3 flex items-center gap-1">
                              <Info className="h-3 w-3 shrink-0" />
                              View, amend, or add sub-clauses for this contract type. Amended items appear highlighted.
                            </p>

                            {allClauses.length === 0 ? (
                              <p className="text-xs text-slate-400 text-center py-4">Loading clauses...</p>
                            ) : (
                              <div className="space-y-1 max-h-[340px] overflow-y-auto pr-1">
                                {allClauses.map((clause) => {
                                  const isExpanded = expandedClauses[clause.clause_number];
                                  const cc = getCustomClauses();
                                  const hasAmendments = !!(cc[clause.clause_number]?.amended_sub_clauses && Object.keys(cc[clause.clause_number].amended_sub_clauses).length > 0);
                                  const hasAdditions = !!(cc[clause.clause_number]?.added_sub_clauses && Object.keys(cc[clause.clause_number].added_sub_clauses).length > 0);
                                  const isModified = hasAmendments || hasAdditions;

                                  // Merge default sub-clauses with added custom ones
                                  const effectiveSubClauses = [...(clause.sub_clauses || [])];
                                  const addedSubs = cc[clause.clause_number]?.added_sub_clauses || {};
                                  Object.entries(addedSubs).forEach(([num, text]) => {
                                    if (!effectiveSubClauses.find(s => s.sub_clause_number === num)) {
                                      effectiveSubClauses.push({ sub_clause_number: num, text, is_custom: true });
                                    }
                                  });

                                  return (
                                    <div key={clause.clause_number} className={`border rounded-sm ${isModified ? 'border-amber-300 bg-amber-50/30' : 'border-slate-100'}`}>
                                      <button
                                        type="button"
                                        onClick={() => setExpandedClauses(prev => ({ ...prev, [clause.clause_number]: !prev[clause.clause_number] }))}
                                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50/50 transition-colors"
                                        data-testid={`clause-toggle-${clause.clause_number}`}
                                      >
                                        <span className="flex items-center gap-2 min-w-0">
                                          <span className="text-[10px] font-mono font-bold text-slate-500 shrink-0">{clause.clause_number}</span>
                                          <span className="text-xs text-slate-700 truncate">{clause.title}</span>
                                          {isModified && <span className="text-[8px] bg-amber-200 text-amber-800 px-1 rounded shrink-0">AMENDED</span>}
                                          {effectiveSubClauses.length > 0 && (
                                            <span className="text-[9px] text-slate-400 shrink-0">({effectiveSubClauses.length})</span>
                                          )}
                                        </span>
                                        {isExpanded ? <ChevronUp className="h-3 w-3 text-slate-400 shrink-0" /> : <ChevronDown className="h-3 w-3 text-slate-400 shrink-0" />}
                                      </button>

                                      {isExpanded && (
                                        <div className="px-3 pb-3 space-y-1.5 border-t border-slate-100">
                                          {/* Main clause text */}
                                          <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">{clause.text}</p>

                                          {/* Sub-clauses */}
                                          {effectiveSubClauses.map((sub) => {
                                            const subNum = sub.sub_clause_number;
                                            const amended = isSubClauseAmended(clause.clause_number, subNum);
                                            const added = isSubClauseAdded(clause.clause_number, subNum) || sub.is_custom;
                                            const effectiveText = getEffectiveSubClauseText(clause.clause_number, subNum, sub.text);
                                            const isEditing = editingSubClause === `${clause.clause_number}:${subNum}`;

                                            return (
                                              <div key={subNum} className={`flex gap-2 items-start py-1.5 px-2 rounded ${amended ? 'bg-amber-50 border border-amber-200' : added ? 'bg-sky-50 border border-sky-200' : 'bg-slate-50'}`}>
                                                <span className="text-[9px] font-mono font-bold text-slate-500 shrink-0 mt-0.5 min-w-[55px]">{subNum}</span>
                                                {isEditing ? (
                                                  <div className="flex-1 space-y-1">
                                                    <textarea
                                                      value={editText}
                                                      onChange={(e) => setEditText(e.target.value)}
                                                      className="w-full text-[10px] border border-slate-300 rounded px-2 py-1 min-h-[48px] focus:outline-none focus:ring-1 focus:ring-slate-400"
                                                      data-testid={`edit-subclause-${subNum}`}
                                                    />
                                                    <div className="flex gap-1">
                                                      <button type="button" onClick={() => handleAmendSubClause(clause.clause_number, subNum, editText)} className="text-[9px] bg-slate-900 text-white px-2 py-0.5 rounded hover:bg-slate-700" data-testid={`save-subclause-${subNum}`}>Save</button>
                                                      <button type="button" onClick={() => { setEditingSubClause(null); setEditText(''); }} className="text-[9px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded hover:bg-slate-300">Cancel</button>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <div className="flex-1 flex items-start justify-between gap-1">
                                                    <span className="text-[10px] text-slate-600 leading-relaxed">{effectiveText}</span>
                                                    <div className="flex gap-0.5 shrink-0">
                                                      <button type="button" onClick={() => { setEditingSubClause(`${clause.clause_number}:${subNum}`); setEditText(effectiveText); }} className="text-slate-400 hover:text-slate-700 p-0.5" title="Amend" data-testid={`amend-subclause-${subNum}`}>
                                                        <Edit2 className="h-3 w-3" />
                                                      </button>
                                                      {(amended || added) && (
                                                        <button type="button" onClick={() => handleRemoveCustomSubClause(clause.clause_number, subNum)} className="text-red-400 hover:text-red-600 p-0.5" title={added ? 'Remove' : 'Revert'} data-testid={`revert-subclause-${subNum}`}>
                                                          <XCircle className="h-3 w-3" />
                                                        </button>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}

                                          {/* Add sub-clause form */}
                                          {addingToClause === clause.clause_number ? (
                                            <div className="mt-2 p-2 border border-dashed border-sky-300 rounded bg-sky-50/50 space-y-1.5">
                                              <div className="flex gap-2">
                                                <Input
                                                  value={newSubClauseNum}
                                                  onChange={(e) => setNewSubClauseNum(e.target.value)}
                                                  placeholder={`e.g. ${clause.clause_number}(${effectiveSubClauses.length + 1})`}
                                                  className="rounded-sm text-[10px] h-7 w-28"
                                                  data-testid={`new-subclause-num-${clause.clause_number}`}
                                                />
                                              </div>
                                              <textarea
                                                value={newSubClauseText}
                                                onChange={(e) => setNewSubClauseText(e.target.value)}
                                                placeholder="Sub-clause text..."
                                                className="w-full text-[10px] border border-slate-300 rounded px-2 py-1 min-h-[40px] focus:outline-none focus:ring-1 focus:ring-sky-400"
                                                data-testid={`new-subclause-text-${clause.clause_number}`}
                                              />
                                              <div className="flex gap-1">
                                                <button type="button" onClick={() => handleAddSubClause(clause.clause_number)} className="text-[9px] bg-sky-600 text-white px-2 py-0.5 rounded hover:bg-sky-700" data-testid={`confirm-add-subclause-${clause.clause_number}`}>Add</button>
                                                <button type="button" onClick={() => { setAddingToClause(null); setNewSubClauseNum(''); setNewSubClauseText(''); }} className="text-[9px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded hover:bg-slate-300">Cancel</button>
                                              </div>
                                            </div>
                                          ) : (
                                            <button
                                              type="button"
                                              onClick={() => setAddingToClause(clause.clause_number)}
                                              className="mt-1 text-[9px] text-sky-600 hover:text-sky-800 flex items-center gap-1 font-medium"
                                              data-testid={`add-subclause-btn-${clause.clause_number}`}
                                            >
                                              <Plus className="h-3 w-3" /> Add Sub-Clause
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3">
                        Create Project
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <div className="space-y-3">
              {projects.map((project) => {
                const client = clients.find(c => c.id === project.client_id);
                return (
                  <div 
                    key={project.id} 
                    className="bg-white border border-slate-200 rounded-sm shadow-sm p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        <h4 className="font-bold text-lg">{project.name}</h4>
                        <p className="text-sm text-slate-600">Client: {client?.name || 'Unknown'}</p>
                        {project.project_number && <p className="text-sm text-slate-500">Project #: {project.project_number}</p>}
                        <p className="text-sm text-slate-500 font-mono">{project.contract_type}</p>
                      </div>
                      {isAdmin() && (
                        <div className="flex items-center gap-1">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditProjectForm({
                                id: project.id,
                                name: project.name || '',
                                client_id: project.client_id || '',
                                description: project.description || '',
                                contract_type: project.contract_type || 'JBCC',
                                contract_number: project.contract_number || '',
                                project_number: project.project_number || '',
                                contractor_name: project.contractor_name || '',
                                location: project.location || null,
                              });
                              setEditProjectDialogOpen(true);
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                            data-testid={`edit-project-${project.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm(`Are you sure you want to delete project "${project.name}"? This will also delete all events, notices, claims, and RFIs.`)) {
                                try {
                                  await api.projects.delete(project.id);
                                  toast.success('Project deleted');
                                  loadData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Failed to delete project');
                                }
                              }
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'settings' && canManageSMTP() && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-2xl font-barlow font-semibold uppercase">Email Settings</h3>
                <p className="text-sm text-slate-500 mt-1">Configure system-wide or per-client SMTP settings for email notifications</p>
              </div>
            </div>
            
            {/* System-wide SMTP Configuration */}
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-6 mb-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-bold text-lg text-blue-900">System Default Email</h4>
                    {systemSmtp ? (
                      <span className="flex items-center gap-1 text-xs uppercase tracking-wider px-2 py-1 rounded bg-emerald-100 text-emerald-700">
                        <CheckCircle className="h-3 w-3" />
                        Configured
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs uppercase tracking-wider px-2 py-1 rounded bg-orange-100 text-orange-700">
                        <XCircle className="h-3 w-3" />
                        Not Configured
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-blue-700 mb-3">
                    This email will be used for all system notifications across all clients. Per-client settings below will override this for specific clients.
                  </p>
                  {systemSmtp && (
                    <div className="p-3 bg-white/70 rounded-sm">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-slate-500 uppercase">Host</p>
                          <p className="font-mono">{systemSmtp.smtp_host}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase">Port</p>
                          <p className="font-mono">{systemSmtp.smtp_port}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase">From Email</p>
                          <p className="font-mono truncate">{systemSmtp.from_email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase">From Name</p>
                          <p className="font-mono">{systemSmtp.from_name || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <Dialog open={systemSmtpDialogOpen} onOpenChange={setSystemSmtpDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        onClick={() => {
                          if (systemSmtp) {
                            setSmtpForm({
                              smtp_host: systemSmtp.smtp_host || '',
                              smtp_port: systemSmtp.smtp_port || 587,
                              smtp_user: systemSmtp.smtp_user || '',
                              smtp_password: '',
                              from_email: systemSmtp.from_email || '',
                              from_name: systemSmtp.from_name || '',
                              use_tls: systemSmtp.use_tls !== false
                            });
                          }
                        }}
                        className="bg-blue-600 text-white hover:bg-blue-700 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                      >
                        {systemSmtp ? 'Edit System Email' : 'Configure System Email'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-barlow font-bold uppercase">System Default SMTP</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSaveSystemSmtp} className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                          <p className="text-sm text-blue-700">
                            This email configuration will be used for all system emails sent on behalf of all clients.
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">SMTP Host</Label>
                            <Input
                              value={smtpForm.smtp_host}
                              onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })}
                              placeholder="smtp.gmail.com"
                              required
                              className="rounded-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">SMTP Port</Label>
                            <Input
                              type="number"
                              value={smtpForm.smtp_port}
                              onChange={(e) => setSmtpForm({ ...smtpForm, smtp_port: parseInt(e.target.value) || 587 })}
                              required
                              className="rounded-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">SMTP Username</Label>
                            <Input
                              value={smtpForm.smtp_user}
                              onChange={(e) => setSmtpForm({ ...smtpForm, smtp_user: e.target.value })}
                              placeholder="your-email@gmail.com"
                              required
                              className="rounded-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">SMTP Password</Label>
                            <Input
                              type="password"
                              value={smtpForm.smtp_password}
                              onChange={(e) => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })}
                              placeholder={systemSmtp ? '(unchanged)' : ''}
                              required={!systemSmtp}
                              className="rounded-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">From Email</Label>
                            <Input
                              type="email"
                              value={smtpForm.from_email}
                              onChange={(e) => setSmtpForm({ ...smtpForm, from_email: e.target.value })}
                              placeholder="noreply@yourdomain.com"
                              required
                              className="rounded-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">From Name</Label>
                            <Input
                              value={smtpForm.from_name}
                              onChange={(e) => setSmtpForm({ ...smtpForm, from_name: e.target.value })}
                              placeholder="DelayClaims System"
                              className="rounded-sm"
                            />
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="system-use-tls"
                            checked={smtpForm.use_tls}
                            onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, use_tls: checked })}
                          />
                          <Label htmlFor="system-use-tls" className="text-sm font-medium cursor-pointer">
                            Use TLS encryption
                          </Label>
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                        >
                          Save System SMTP Settings
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  {systemSmtp && (
                    <Button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete the system SMTP settings?')) {
                          try {
                            await api.smtp.deleteSystemSettings();
                            toast.success('System SMTP settings deleted');
                            loadData();
                          } catch (error) {
                            toast.error('Failed to delete settings');
                          }
                        }
                      }}
                      variant="outline"
                      className="border-red-600 text-red-600 hover:bg-red-50 rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-3"
                    >
                      Delete
                    </Button>
                  )}
                  {systemSmtp && (
                    <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          onClick={() => setTestEmailAddress(user?.email || '')}
                          variant="outline"
                          className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-3"
                        >
                          Send Test Email
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase">Send Test Email</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <p className="text-sm text-slate-600">
                            Send a test email to verify your SMTP configuration is working correctly.
                          </p>
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                              Email Address
                            </Label>
                            <Input
                              type="email"
                              value={testEmailAddress}
                              onChange={(e) => setTestEmailAddress(e.target.value)}
                              placeholder="your@email.com"
                              className="rounded-sm"
                            />
                          </div>
                          <Button
                            onClick={async () => {
                              if (!testEmailAddress) {
                                toast.error('Please enter an email address');
                                return;
                              }
                              setSendingTestEmail(true);
                              try {
                                await api.smtp.testSystemEmail(testEmailAddress);
                                toast.success(`Test email sent to ${testEmailAddress}`);
                                setTestEmailDialogOpen(false);
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to send test email');
                              } finally {
                                setSendingTestEmail(false);
                              }
                            }}
                            disabled={sendingTestEmail || !testEmailAddress}
                            className="w-full bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                          >
                            {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 mb-6">
              <p className="text-sm text-emerald-700">
                <strong>Real SMTP Enabled:</strong> Emails will be sent using the configured SMTP settings. 
                Use the "Send Test Email" button to verify your configuration.
              </p>
            </div>

            {/* Per-Client SMTP Settings */}
            <div className="flex justify-between items-center mb-4">
              <div>
                <h4 className="text-lg font-barlow font-semibold uppercase">Per-Client Overrides (Optional)</h4>
                <p className="text-sm text-slate-500">Configure specific SMTP settings for individual clients</p>
              </div>
              <Dialog open={smtpDialogOpen} onOpenChange={setSmtpDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-smtp-button" 
                    variant="outline"
                    className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Client Override
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-barlow font-bold uppercase">Configure Client SMTP</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveSmtpSettings} className="space-y-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Client</Label>
                      <Select value={selectedClientForSmtp} onValueChange={setSelectedClientForSmtp}>
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="Select a client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">SMTP Host</Label>
                        <Input
                          value={smtpForm.smtp_host}
                          onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })}
                          placeholder="smtp.example.com"
                          required
                          className="rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">SMTP Port</Label>
                        <Input
                          type="number"
                          value={smtpForm.smtp_port}
                          onChange={(e) => setSmtpForm({ ...smtpForm, smtp_port: parseInt(e.target.value) || 587 })}
                          required
                          className="rounded-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">SMTP Username</Label>
                        <Input
                          value={smtpForm.smtp_user}
                          onChange={(e) => setSmtpForm({ ...smtpForm, smtp_user: e.target.value })}
                          required
                          className="rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">SMTP Password</Label>
                        <Input
                          type="password"
                          value={smtpForm.smtp_password}
                          onChange={(e) => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })}
                          required
                          className="rounded-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">From Email</Label>
                        <Input
                          type="email"
                          value={smtpForm.from_email}
                          onChange={(e) => setSmtpForm({ ...smtpForm, from_email: e.target.value })}
                          placeholder="noreply@example.com"
                          required
                          className="rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">From Name</Label>
                        <Input
                          value={smtpForm.from_name}
                          onChange={(e) => setSmtpForm({ ...smtpForm, from_name: e.target.value })}
                          placeholder="DelayClaims System"
                          className="rounded-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="use-tls"
                        checked={smtpForm.use_tls}
                        onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, use_tls: checked })}
                      />
                      <Label htmlFor="use-tls" className="text-sm font-medium cursor-pointer">
                        Use TLS encryption
                      </Label>
                    </div>
                    <Button 
                      type="submit" 
                      disabled={!selectedClientForSmtp}
                      className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                    >
                      Save SMTP Settings
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {clients.map((client) => {
                const clientSmtp = smtpSettings.find(s => s.client_id === client.id);
                return (
                  <div key={client.id} className="bg-white border border-slate-200 rounded-sm shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-bold text-lg">{client.name}</h4>
                          {clientSmtp ? (
                            <span className="flex items-center gap-1 text-xs uppercase tracking-wider px-2 py-1 rounded bg-purple-100 text-purple-700">
                              <CheckCircle className="h-3 w-3" />
                              Custom SMTP
                            </span>
                          ) : systemSmtp ? (
                            <span className="flex items-center gap-1 text-xs uppercase tracking-wider px-2 py-1 rounded bg-blue-100 text-blue-700">
                              <CheckCircle className="h-3 w-3" />
                              Uses System Default
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs uppercase tracking-wider px-2 py-1 rounded bg-slate-100 text-slate-500">
                              <XCircle className="h-3 w-3" />
                              No Email Config
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{client.email}</p>
                        {clientSmtp && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-sm">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-xs text-slate-500 uppercase">Host</p>
                                <p className="font-mono">{clientSmtp.smtp_host}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 uppercase">Port</p>
                                <p className="font-mono">{clientSmtp.smtp_port}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 uppercase">From</p>
                                <p className="font-mono truncate">{clientSmtp.from_email}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 uppercase">TLS</p>
                                <p className="font-mono">{clientSmtp.use_tls ? 'Yes' : 'No'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setSelectedClientForSmtp(client.id);
                            if (clientSmtp) {
                              setSmtpForm({
                                smtp_host: clientSmtp.smtp_host,
                                smtp_port: clientSmtp.smtp_port,
                                smtp_user: clientSmtp.smtp_user,
                                smtp_password: '',
                                from_email: clientSmtp.from_email,
                                from_name: clientSmtp.from_name || '',
                                use_tls: clientSmtp.use_tls
                              });
                            }
                            setSmtpDialogOpen(true);
                          }}
                          variant="outline"
                          className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-2"
                        >
                          {clientSmtp ? 'Edit' : 'Configure'}
                        </Button>
                        {clientSmtp && (
                          <Button
                            onClick={async () => {
                              try {
                                await api.smtp.deleteSettings(client.id);
                                toast.success('SMTP settings deleted');
                                loadData();
                              } catch (error) {
                                toast.error('Failed to delete settings');
                              }
                            }}
                            variant="outline"
                            className="border-red-600 text-red-600 hover:bg-red-50 rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-2"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'event-config' && isAdmin() && (
          <EventConfigPanel />
        )}
      </div>
      <Dialog open={editProjectDialogOpen} onOpenChange={setEditProjectDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-project-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold uppercase tracking-wider">Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Project Name</Label>
                <Input data-testid="edit-proj-name" value={editProjectForm.name} onChange={e => setEditProjectForm(f => ({...f, name: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Client</Label>
                <Select value={editProjectForm.client_id} onValueChange={v => setEditProjectForm(f => ({...f, client_id: v}))}>
                  <SelectTrigger data-testid="edit-proj-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Description</Label>
              <Textarea data-testid="edit-proj-desc" value={editProjectForm.description} onChange={e => setEditProjectForm(f => ({...f, description: e.target.value}))} rows={2} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Contract Type</Label>
                <Select value={editProjectForm.contract_type} onValueChange={v => setEditProjectForm(f => ({...f, contract_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="JBCC">JBCC</SelectItem>
                    <SelectItem value="NEC">NEC</SelectItem>
                    <SelectItem value="FIDIC">FIDIC</SelectItem>
                    <SelectItem value="GCC">GCC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Contract Number</Label>
                <Input value={editProjectForm.contract_number} onChange={e => setEditProjectForm(f => ({...f, contract_number: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Project Number</Label>
                <Input value={editProjectForm.project_number} onChange={e => setEditProjectForm(f => ({...f, project_number: e.target.value}))} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Contractor Name</Label>
              <Input value={editProjectForm.contractor_name} onChange={e => setEditProjectForm(f => ({...f, contractor_name: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Location</Label>
              <LocationAutocomplete
                value={editProjectForm.location}
                onChange={loc => setEditProjectForm(f => ({...f, location: loc}))}
              />
              {editProjectForm.location?.address && (
                <p className="text-xs text-slate-500 mt-1">{editProjectForm.location.address}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditProjectDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button
                className="rounded-sm"
                data-testid="save-edit-project-btn"
                onClick={async () => {
                  try {
                    const { id, ...data } = editProjectForm;
                    await api.projects.update(id, data);
                    toast.success('Project updated');
                    setEditProjectDialogOpen(false);
                    loadData();
                  } catch (err) {
                    toast.error('Failed to update project');
                  }
                }}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
}
